import { useAppSelector } from 'appState/store'
import { useState } from 'react'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import { Tooltip } from '@outbound-ai/ursa'
import styled, { css } from 'styled-components'
import { PositionEnum } from 'types/cssTypes'

import { useUserProfile } from '../settingsHooks'
import { SettingsAudioDevices } from './SettingsAudioDevices'
import { SettingsDiagnosticCall } from './SettingsDiagnosticCall'
import { SettingsDiagnosticNetwork } from './SettingsDiagnosticNetwork'

export const SettingsDiagnostic = () => {
  const [tabIndex, setTabIndex] = useState(0)
  const { user } = useAppSelector((state) => state.auth)
  const { data: profile } = useUserProfile(user?.id)
  const tenants = profile?.tenants
  return (
    <Section tabIndex={tabIndex}>
      <h2>Diagnostics</h2>
      <hr />
      <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
        <TabList>
          <Tab>Audio</Tab>
          <Tab>Network</Tab>
          <Tab disabled={tenants?.length == 0}>
            <Tooltip
              tooltipText={
                tenants?.length == 0 ? 'This feature is available only for users that have a practice assigned' : ''
              }
              position={PositionEnum.top}
              positionContent={100}
              positionTooltipArrow={30}
              tooltipStyles={{ 'min-width': '15rem', cursor: 'not-allowed' }}
            >
              <>Test Calls</>
            </Tooltip>
          </Tab>
        </TabList>
        <TabPanel>
          <SettingsAudioDevices />
        </TabPanel>
        <TabPanel>
          <SettingsDiagnosticNetwork />
        </TabPanel>
        <TabPanel>
          <SettingsDiagnosticCall />
        </TabPanel>
      </Tabs>
    </Section>
  )
}

const Section = styled.section<{ tabIndex: number }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  h2 {
    font-size: ${({ theme }) => theme.typography.h5.bold};
    color: ${({ theme }) => theme.colors.primary.darker};
    margin-bottom: 0px;
  }

  hr {
    height: 1px;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.grey.light};
  }

  .react-tabs {
    display: inherit;
    align-items: center;
    flex-direction: column;
    justify-self: center;
    align-self: center;
    ul.react-tabs__tab-list {
      display: flex;
      border: 1px solid ${({ theme }) => theme.colors.primary.neutral};
      border-radius: ${({ theme }) => theme.borderRadius.normal};
      font-size: 1rem;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
      color: ${({ theme }) => theme.colors.primary.darker};
      cursor: pointer;
      margin-bottom: 2rem;
      .react-tabs__tab {
        padding: 0.25rem ${({ theme }) => theme.util.pixelToRem(50)};
        border-right: 1px solid ${({ theme }) => theme.colors.primary.neutral};
      }

      .react-tabs__tab:last-child {
        border-right: none;
      }
      .react-tabs__tab--selected {
        outline: none;
        font-weight: ${({ theme }) => theme.fontWeight.bolder};
        background-color: ${({ theme }) => theme.colors.primary.lightest};

        ${({ theme, tabIndex }) => {
          switch (tabIndex) {
            case 0:
              return css`
                border-top-left-radius: ${theme.borderRadius.normal};
                border-bottom-left-radius: ${theme.borderRadius.normal};
              `
            case 2:
              return css`
                border-top-right-radius: ${theme.borderRadius.normal};
                border-bottom-right-radius: ${theme.borderRadius.normal};
              `
            default:
              return null
          }
        }};
      }
    }

    .react-tabs__tab-panel--selected {
      padding: 2rem;
      border-radius: ${({ theme }) => theme.borderRadius.rounded};
      ${({ tabIndex }) =>
        tabIndex === 2
          ? css`
              padding: 0rem;
            `
          : null}
    }
  }
`
